import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { isPlanLimitError } from '@/lib/permissions/quotaError';
import { useT } from '@/lib/i18n/useT';

/**
 * Create N events at once. Names auto-number Event1..EventN and dates spread
 * across the parent project range — the backend computes both, plus one
 * default station per event. Plan caps abort the batch on the first overflow.
 */
export function BatchCreateEventsDialog({
  open,
  onOpenChange,
  groupId,
  companyId,
  projectStart,
  projectEnd,
  existingCount,
  onLimitReached,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  groupId: number;
  companyId?: number;
  projectStart: string | null | undefined;
  projectEnd: string | null | undefined;
  existingCount: number;
  onLimitReached: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [count, setCount] = useState(3);

  const create = useMutation({
    mutationFn: () =>
      eventsApi.createEventsBatch({ eventGroupId: groupId, count, companyId }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byGroup(groupId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('planner.batch.createdToast', { count: String(res.created) }) });
      onOpenChange(false);
    },
    onError: (e) => {
      if (isPlanLimitError(e)) {
        onOpenChange(false);
        onLimitReached();
        return;
      }
      toast({ tone: 'error', title: t('planner.batch.failedToast'), description: (e as Error).message });
    },
  });

  const rangeLabel =
    projectStart && projectEnd
      ? `${dayjs(projectStart).format('MMM D')} – ${dayjs(projectEnd).format('MMM D, YYYY')}`
      : t('planner.batch.noRange');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('planner.batch.title')}</DialogTitle>
          <DialogDescription>{t('planner.batch.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FormField label={t('planner.batch.countLabel')} required>
            <Input
              type="number"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </FormField>
          <div className="rounded-lg border border-dashed border-[var(--color-xk-border)] bg-[var(--color-bg-base)] p-3 text-[11px] text-[var(--color-xk-text-muted)]">
            <p>
              {t('planner.batch.previewNames', {
                first: `Event${existingCount + 1}`,
                last: `Event${existingCount + count}`,
              })}
            </p>
            <p className="mt-1">{t('planner.batch.previewRange', { range: rangeLabel })}</p>
            <p className="mt-1">{t('planner.batch.previewStation')}</p>
          </div>
          <div className="flex justify-end gap-3">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
              >
                {t('planner.batch.cancel')}
              </button>
            </DialogClose>
            <Button onClick={() => create.mutate()} loading={create.isPending}>
              {t('planner.batch.create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
